import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import BadgeRow from "@/components/BadgeRow";

/**
 * UserSearchModal — global user lookup.
 * Triggered from the SEARCH button in the Dashboard top toolbar AND from the
 * Admin Module (right of Refresh). Debounced — fires once typing pauses for 250ms.
 * Hardened against backend-not-deployed scenarios (404) and bad payload shapes.
 */
export default function UserSearchModal({ onClose, onSelect }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [softErr, setSoftErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); setSoftErr(""); return; }
    setLoading(true); setSoftErr("");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/social/search/users", { params: { q, limit: 12 } });
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        // Don't crash — surface a friendly hint instead. Most common cause in
        // production is a stale deploy that doesn't have the search endpoint yet.
        const status = e?.response?.status;
        setRows([]);
        if (status === 404) {
          setSoftErr("Search isn't available on this build. Redeploy your app to enable it.");
        } else if (status) {
          setSoftErr(`Search error ${status}. Try again in a moment.`);
        } else {
          setSoftErr("Network error — check your connection.");
        }
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  function safeSelect(r) {
    try { onSelect && onSelect(r); } catch {}
    try { onClose(); } catch {}
  }

  return (
    <div data-testid="user-search-modal" className="fixed inset-0 bg-black/80 backdrop-blur-md z-[60] flex items-start justify-center p-4 pt-20 font-mono" onClick={onClose}>
      <div className="bg-[#0f131b] border border-slate-800 rounded-2xl max-w-md w-full p-4 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-3 pb-2 border-b border-slate-800">
          <i className="fa-solid fa-magnifying-glass text-slate-500"/>
          <input
            ref={inputRef}
            data-testid="search-input"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="flex-1 bg-transparent text-sm text-white outline-none placeholder-slate-600"
          />
          <button onClick={onClose} className="text-slate-500 hover:text-rose-300 text-xs">ESC</button>
        </div>
        {loading && <div className="text-xs text-slate-500 py-3 text-center">Searching…</div>}
        {softErr && (
          <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1.5 mb-2">
            <i className="fa-solid fa-circle-info mr-1"/>{softErr}
          </div>
        )}
        {!loading && !softErr && q.trim().length >= 2 && rows.length === 0 && (
          <div className="text-xs text-slate-500 py-3 text-center">No matches.</div>
        )}
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {rows.map(r => {
            if (!r?.id) return null;
            return (
              <Link
                key={r.id}
                to={`/u/${r.id}`}
                data-testid={`search-result-${r.id}`}
                onClick={() => safeSelect(r)}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800/60 cursor-pointer"
              >
                {r.avatar
                  ? <img src={r.avatar} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-700"/>
                  : <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[10px] text-slate-500">{(r.name||r.email||"?").charAt(0).toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-white font-bold flex items-center gap-1.5">
                    <span className="truncate">{r.name || r.email || "—"}</span>
                    {/* BadgeRow guards itself — wrap in try just in case */}
                    {(() => { try { return <BadgeRow userId={r.id} size="sm"/>; } catch { return null; } })()}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">{r.email || ""}{r.role ? ` · ${r.role}` : ""}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
